import * as React from 'react';
import styled from 'styled-components';
import { debounce, unionWith } from 'lodash';
import { LayoutSidebar } from 'layout-sidebar';
import { Grid } from 'layout-core-components';
import { colors } from 'bench-style-constants';
import { Button, ComboBoxInput, Text } from 'bench-internal-theme';

import Header from './Header';
import ReviewSectionContainer from './ReviewSectionContainer';
import BeforeUnload from './BeforeUnload';
import Title from './Title';
import YearMonthString from './YearMonthString';
import TransactionInfoContainer from './TransactionInfoContainer';
import TransactionInfoCard from './TransactionInfoCard';
import LedgerInfoContainer from './LedgerInfoContainer';
import LedgerInfoCard from './LedgerInfoCard';
import InternalNotesContainer from './InternalNotesContainer';
import { Checkbox, ListItemText, MenuItem, Select } from '@material-ui/core';
import { ReviewSection } from './types';

const reviewTypeOptions = [
  { label: 'Monthly self review', value: 'MONTHLY_SELF_REVIEW' },
  { label: 'Monthly peer review', value: 'MONTHLY_PEER_REVIEW' },
  { label: 'Annual self review', value: 'ANNUAL_SELF_REVIEW' },
  { label: 'Annual peer review', value: 'ANNUAL_PEER_REVIEW' },
];

const Wrapper = styled.div`
  display: grid;
  grid-template-rows: auto 1fr auto;
  height: 100vh;
`;

const GridWrapper = styled.div`
  overflow-y: auto;
`;

const HeaderLayout = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  flex: 1;
`;

const CloseButton = styled.div`
  color: ${colors.cloud};
  font-size: 12px;
  text-transform: uppercase;
  align-self: center;
  cursor: pointer;
`;

const SummaryWrapper = styled.div`
  padding: 12px 32px;
  background: ${colors.white};
  font-size: 12px;
  line-height: 12px;
  border-top: 1px solid ${colors.silt};
  display: grid;
  grid-template-columns: 1fr auto;
  grid-column-gap: 12px;
`;

const Notes = styled.textarea`
  // "width: 100%" combined with horizontal padding or borders requires "box-sizing: border-box" to
  // prevent horizontal overflow
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  resize: vertical;
  padding: 16px;
  border: solid 1px ${colors.silt};
  border-radius: 4px;
`;

const SectionWrapper = styled.div`
  overflow-y: auto;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 12px;
  min-width: 160px;
`;

type ReviewToolProps = {
  reviewSections: ReviewSection[];
  clientId: string;
  startDate: string;
  endDate: string;
  restoreLastChecks: boolean;
  onCloseReview: () => void;
  submitReview: (
    notes: string,
    checks: Record<string, unknown>[],
    type: string,
    requireFixes: boolean,
    requireCategorizationFixes: boolean,
    requireAccountBalanceFixes: boolean,
    requireIncorrectBooksFixes: boolean,
  ) => void;
  isFixIncorrectBooksCheckboxEnabled: boolean;
};

type ReviewToolState = {
  notes: string;
  checks: Record<string, unknown>[];
  typeOption: { label: string; value: string } | null;
  hasChanged: boolean;
  journalEntryId: string;
  ledgerId: string;
  requireFixes: boolean;
  requireCategorizationFixes: boolean;
  requireAccountBalanceFixes: boolean;
  requireIncorrectBooksFixes: boolean;
};

const DropdownMenuProps = {
  PaperProps: {
    style: {
      width: '220px',
    },
  },
};

class ReviewTool extends React.Component<ReviewToolProps, ReviewToolState> {
  state = {
    notes: '',
    checks: [],
    typeOption: { label: '', value: '' },
    hasChanged: false,
    journalEntryId: '',
    ledgerId: '',
    requireFixes: false,
    requireCategorizationFixes: false,
    requireAccountBalanceFixes: false,
    requireIncorrectBooksFixes: false,
  };

  onCheckStatusListUpdate = (checkStatusList: Record<string, unknown>[], setByUser?: boolean) => {
    this.setState(prevState => {
      // Merge the current status list with the provided one.
      // If a check already exists, replace its status.
      const checks = unionWith(
        checkStatusList,
        prevState.checks,
        (newCheck, prevCheck) =>
          newCheck.checkId === prevCheck.checkId && newCheck.ledgerId === prevCheck.ledgerId,
      );

      return { checks };
    });

    if (setByUser) {
      this.setState({ hasChanged: true });
    }
  };

  onSubmit = () => {
    const {
      notes,
      checks,
      typeOption,
      requireFixes,
      requireCategorizationFixes,
      requireAccountBalanceFixes,
      requireIncorrectBooksFixes,
    } = this.state;

    if (!typeOption) {
      return;
    }

    const { submitReview } = this.props;

    submitReview(
      notes,
      checks,
      typeOption.value,
      requireFixes,
      requireCategorizationFixes,
      requireAccountBalanceFixes,
      requireIncorrectBooksFixes,
    );
  };

  onNotesChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const notes = event.target.value;
    this.updateNotes(notes);
  };

  onSidebarToggle = (data: { journalEntryId?: string; ledgerId?: string }) => {
    const { journalEntryId, ledgerId } = data;

    const isNewJournalEntryId = journalEntryId && journalEntryId !== this.state.journalEntryId;
    const isNewLedgerId = ledgerId && ledgerId !== this.state.ledgerId;

    return this.setState({
      journalEntryId: isNewJournalEntryId ? journalEntryId : '',
      ledgerId: isNewLedgerId ? ledgerId : '',
    });
  };

  updateNotes = debounce(notes => {
    this.setState({ notes, hasChanged: true });
  }, 400);

  onFixesChange = (
    event: React.ChangeEvent<{
      name?: string;
      value: unknown;
    }>,
  ) => {
    const { value } = event.target;
    const values = value as string[];

    this.setState({
      requireFixes: values.includes('requireFixes'),
      requireCategorizationFixes: values.includes('requireCategorizationFixes'),
      requireAccountBalanceFixes: values.includes('requireAccountBalanceFixes'),
      requireIncorrectBooksFixes: values.includes('requireIncorrectBooksFixes'),
    });
  };

  SidebarRenderer = () => {
    const { clientId } = this.props;

    const { journalEntryId, ledgerId } = this.state;

    if (journalEntryId) {
      return (
        <TransactionInfoContainer journalEntryId={journalEntryId}>
          {(loading, error, transaction, refetchQueries) => {
            if (loading || error) {
              return <TransactionInfoCard loading={loading} error={error} />;
            }

            const {
              id,
              amount,
              vendor,
              dataEntries,
              documentDate,
              internalNotes,
              journalEntryId: transactionJeId,
            } = transaction;

            return (
              <GridWrapper>
                <Grid column="auto 1fr">
                  <TransactionInfoCard
                    id={id as string}
                    clientId={clientId}
                    amount={amount}
                    vendor={vendor}
                    dataEntries={dataEntries}
                    documentDate={documentDate}
                    loading={loading}
                    error={error}
                  />
                  <InternalNotesContainer
                    journalEntryId={transactionJeId}
                    internalNotes={internalNotes}
                    refetchQueries={refetchQueries}
                  />
                </Grid>
              </GridWrapper>
            );
          }}
        </TransactionInfoContainer>
      );
    }

    if (ledgerId) {
      return (
        <LedgerInfoContainer
          ledgerId={ledgerId}
          clientId={clientId}
          data-test-id="review-form-ledger-sidebar"
        >
          {(loading, error, ledgerInfo, refetchQueries) => {
            if (loading || error) {
              return <LedgerInfoCard loading={loading} error={error} />;
            }

            if (ledgerInfo) {
              const { name, details, internalNotes } = ledgerInfo;

              return (
                <GridWrapper>
                  <Grid column="auto 1fr">
                    <LedgerInfoCard loading={loading} error={error} name={name} details={details} />
                    <InternalNotesContainer
                      ledgerId={ledgerId}
                      internalNotes={internalNotes}
                      refetchQueries={refetchQueries}
                    />
                  </Grid>
                </GridWrapper>
              );
            }

            return null;
          }}
        </LedgerInfoContainer>
      );
    }

    return null;
  };

  ContentRenderer = () => {
    const { notes, typeOption } = this.state;

    const { reviewSections, clientId, startDate, endDate, restoreLastChecks, onCloseReview } =
      this.props;

    return (
      <Wrapper>
        <Header>
          <HeaderLayout>
            <Title>
              Review for&nbsp;
              <YearMonthString yearMonth={startDate} /> to <YearMonthString yearMonth={endDate} />
            </Title>
            <div />
            {/* This acts as a spacer */}
            <CloseButton onClick={onCloseReview}>Cancel</CloseButton>
          </HeaderLayout>
        </Header>
        <SectionWrapper>
          {reviewSections.map(({ id, name, description }) => (
            <ReviewSectionContainer
              id={id}
              key={id}
              name={name}
              description={description}
              clientId={clientId}
              startDate={startDate}
              endDate={endDate}
              onSidebarToggle={this.onSidebarToggle}
              restoreLastChecks={restoreLastChecks}
              onCheckStatusListUpdate={this.onCheckStatusListUpdate}
            />
          ))}
        </SectionWrapper>
        <SummaryWrapper>
          {/* Wrapping textarea in to allow grammarly to work in textarea */}
          <div>
            <Notes maxLength={2000} placeholder="Add review notes" onChange={this.onNotesChange} />
          </div>
          <ButtonWrapper>
            <div>
              <Text styleType="small" uppercase>
                Review Type (required)
              </Text>
              <ComboBoxInput
                menuPlacement="auto"
                onChange={newTypeOption => this.setState({ typeOption: newTypeOption })}
                options={reviewTypeOptions}
                value={typeOption}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Text as="label" htmlFor="requiresFixes" styleType="small" uppercase>
                Fixes Needed
              </Text>
              <Select
                id="requiresFixes"
                multiple
                value={[
                  this.state.requireFixes ? 'requireFixes' : null,
                  this.state.requireCategorizationFixes ? 'requireCategorizationFixes' : null,
                  this.state.requireAccountBalanceFixes ? 'requireAccountBalanceFixes' : null,
                  this.state.requireIncorrectBooksFixes ? 'requireIncorrectBooksFixes' : null,
                ].filter(v => v !== null)}
                renderValue={(selected: unknown) =>
                  `${(selected as (string | null)[]).length} selected`
                }
                onChange={this.onFixesChange}
                SelectDisplayProps={{
                  style: {
                    padding: '6px 8px',
                    border: '1px solid #B8B7B5',
                    borderRadius: '2px',
                    fontSize: '12px',
                    color: '#2F3034',
                  },
                }}
                MenuProps={DropdownMenuProps}
                disableUnderline
              >
                {this.props.isFixIncorrectBooksCheckboxEnabled ? (
                  <MenuItem value="requireIncorrectBooksFixes">
                    <Checkbox
                      checked={this.state.requireIncorrectBooksFixes}
                      style={{
                        color: '#3D898B',
                      }}
                    />
                    <ListItemText primary="BPA Work Required" />
                  </MenuItem>
                ) : undefined}
                <MenuItem value="requireFixes">
                  <Checkbox
                    checked={this.state.requireFixes}
                    style={{
                      color: '#3D898B',
                    }}
                  />
                  <ListItemText primary="Adjustment" />
                </MenuItem>
                <MenuItem value="requireCategorizationFixes">
                  <Checkbox
                    checked={this.state.requireCategorizationFixes}
                    style={{
                      color: '#3D898B',
                    }}
                  />
                  <ListItemText primary="Categorization" />
                </MenuItem>
                <MenuItem value="requireAccountBalanceFixes">
                  <Checkbox
                    checked={this.state.requireAccountBalanceFixes}
                    style={{
                      color: '#3D898B',
                    }}
                  />
                  <ListItemText primary="Account Balance" />
                </MenuItem>
              </Select>
            </div>

            <Button
              disabled={!typeOption.value}
              onClick={this.onSubmit}
              size="large"
              styleType="primary"
              value={notes}
            >
              Save
            </Button>
          </ButtonWrapper>
        </SummaryWrapper>
      </Wrapper>
    );
  };

  render() {
    const { journalEntryId, ledgerId, hasChanged } = this.state;

    return (
      <BeforeUnload active={hasChanged}>
        <LayoutSidebar
          showSidebar={!!journalEntryId || !!ledgerId}
          sidebarPosition="right"
          sidebarRenderer={this.SidebarRenderer}
          contentRenderer={this.ContentRenderer}
        />
      </BeforeUnload>
    );
  }
}

export default ReviewTool;
